<template>
<tr class="job-param-row-wrapper">
  <td>
    <div v-if="description">
      <v-popover :popover-base-class="'tooltip popover job-description-popover'">
        <i class="fas fa-info-circle"></i>
        <template #popover>
          <p v-html="description.data"></p>
        </template>
      </v-popover>
    </div>
  </td>
  <td>{{param.humanName}}</td>
  <td>
    <b-field :type="{'is-danger': errors.has(validationName)}" :message="errors.first(validationName)">
      <b-input v-if="loading" loading disabled />

      <cytomine-multiselect
        v-else-if="options"
        v-model="internalValue"
        :options="options"
        track-by="id"
        :label="param.uriPrintAttribut"
        :class="{'is-danger': errors.has(validationName)}"
        :multiple="param.type === 'ListDomain'"
        :name="validationName"
        v-validate="validationRules"
      >
        <template #option="{option}">
          <div class="is-flex" v-if="annotationObjects">
            <div class="thumb-wrapper">
              <img class="thumb" :src="option.smallCropURL + '&draw=true&complete=true&increaseArea=1.25'">
            </div>
            <span class="option__title">
              {{ option[param.uriPrintAttribut] }}
            </span>
          </div>
          <div class="is-flex" v-else-if="option.thumb">
            <div class="thumb-wrapper">
              <img class="thumb" :src="option.thumb">
            </div>
            <span class="option__title">
              {{ option[param.uriPrintAttribut] }}
            </span>
          </div>
          <div class="is-flex" v-else-if="option.color">
            <cytomine-term :term="option" />
          </div>
        </template>
      </cytomine-multiselect>

      <b-checkbox v-model="internalValue" v-else-if="param.type === 'Boolean'" />

      <b-input v-else v-model="internalValue" :name="validationName" v-validate="validationRules" />
    </b-field>
  </td>
</tr>
</template>

<script>
import {get} from '@/utils/store-helpers';

import {Cytomine, Description} from 'cytomine-client';
import CytomineMultiselect from '@/components/form/CytomineMultiselect';
import CytomineTerm from '@/components/ontology/CytomineTerm';

export default {
  name: 'job-parameter-row',
  inject: ['$validator'],
  components: {CytomineTerm, CytomineMultiselect},
  props: {
    param: Object,
    value: null
  },
  data() {
    return {
      internalValue: null,
      options: null,
      description: null,
      loading: true
    };
  },
  computed: {
    project: get('currentProject/project'),
    ontology: get('currentProject/ontology'),
    processedUri() {
      if(this.param.uri_) {
        let result = this.param.uri_.replace(new RegExp('^' + Cytomine.instance.basePath), '');
        result = result.replace('$currentProjectCreationDate$', this.project.created);
        result = result.replace('$currentProject$', this.project.id);
        result = result.replace('$cytomineHost$', Cytomine.instance.host);
        result = result.replace('$currentDate$', new Date().getTime());
        result = result.replace('$currentOntology$', this.ontology ? this.ontology.id : null);
        return result;
      }
    },
    validationName() {
      return String(this.param.id);
    },
    validationRules() {
      return {
        required: this.param.required,
        decimal: this.param.type === 'Number'
      };
    },
    annotationObjects() {
      if(this.options && this.options.length > 0) {
        let cytoClass = this.options[0].class;
        return [
          'be.cytomine.ontology.AlgoAnnotation',
          'be.cytomine.ontology.UserAnnotation',
          'be.cytomine.ontology.ReviewedAnnotation'
        ].includes(cytoClass);
      }
    }
  },
  watch: {
    internalValue(value) {
      if(value !== this.value) {
        this.$emit('input', value);
      }
    },
    'param.value': function() {
      this.internalValue = this.param.value;
    }
  },
  methods: {
    getInitialValue() {
      if(this.param.defaultParamValue) {
        if(this.param.type === 'Boolean') {
          return this.param.defaultParamValue.toLowerCase() === 'true';
        }
        return this.param.defaultParamValue;
      }

      switch(this.param.type) {
        case 'ListDomain':
          return [];
        case 'Domain':
          return null;
        case 'Boolean':
          return false;
        default:
          return '';
      }
    }
  },
  async created() {
    this.internalValue = this.getInitialValue();

    try {
      this.description = await Description.fetch(this.param);
    }
    catch(error) {
      // Do nothing as a 404 error means no description
    }

    if(this.processedUri) {
      try {
        let {data} = await Cytomine.instance.api.get(this.processedUri);
        let options = data.collection;
        if(this.param.uriSortAttribut) {
          options.sort((a, b) => a[this.param.uriSortAttribut] < b[this.param.uriSortAttribut] ? -1 : 1);
        }
        this.options = options;
      }
      catch(error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-analysis-parameters-options', {paramName: this.param.name})});
      }
    }
    this.loading = false;
  }
};
</script>

<style lang="scss">
.job-param-row-wrapper {
  .thumb-wrapper {
    width: 4em;
  }

  .thumb  {
    max-height: 5em;
    max-width: 3em;
    background: white;
  }
}

.job-description-popover {
  z-index: 5000 !important;
}

.fas.fa-info-circle {
  cursor: pointer;
}
</style>
